import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../lib/cn";

const typographyVariants = cva("", {
  variants: {
    variant: {
      h1: "text-twi-heading-3xl",
      h2: "text-twi-heading-2xl",
      h3: "text-twi-heading-xl",
      h4: "text-twi-heading-lg",
      h5: "text-twi-heading-md",
      h6: "text-twi-heading-sm",
      body: "text-twi-body",
      blockquote: "border-l-4 pl-4 italic",
    },
  },
  defaultVariants: {
    variant: "body",
  },
});

const variantElementMap = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  body: "p",
  blockquote: "blockquote",
} as const;

type VariantKey = keyof typeof variantElementMap;

type TypographyElement = (typeof variantElementMap)[VariantKey];

export interface TypographyProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof typographyVariants> {
  as?: TypographyElement;
  asChild?: boolean;
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  (
    { className, variant = "body", as: asElement, asChild = false, ...props },
    ref,
  ) => {
    const element =
      asElement ?? variantElementMap[variant as VariantKey] ?? "p";
    const Comp = asChild ? Slot : element;

    return (
      <Comp
        ref={ref as React.Ref<never>}
        className={cn(typographyVariants({ variant, className }))}
        {...props}
      />
    );
  },
);
Typography.displayName = "Typography";

export { Typography, typographyVariants };
